import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, KeyRound, Loader2, ShieldAlert } from "lucide-react";
import { motion } from "framer-motion";
import api, { formatApiError } from "../lib/api";
import { PasswordChecklist, isPasswordValid } from "../components/PasswordChecklist";
import { PasswordField } from "../components/PasswordField";
import { AuthHeroPanel } from "../components/AuthHeroPanel";

const E1_LOGO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_err-dashboard-portal/artifacts/phsk29o5_E1-Primary-Logo_Small_Transparent.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState("");
  const token = searchParams.get("token") || "";

  const submitReset = async (event) => {
    event.preventDefault();
    if (newPassword.length < 12) {
      setError("Choose a password with at least 12 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: newPassword,
      });
      setComplete(true);
    } catch (requestError) {
      setError(
        formatApiError(requestError.response?.data?.detail) ||
          "We could not reset your password. Request a new recovery link."
      );
    } finally {
      setLoading(false);
    }
  };

  const linkMissing = !token;

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5" data-testid="reset-password-page">
      <AuthHeroPanel testId="reset-password-panel" logoTestId="reset-password-brand"
        eyebrow="Account recovery"
        footer={<><KeyRound size={14} /> New password · Existing sessions ended</>}>
        <h1 className="font-heading text-5xl font-light leading-none xl:text-6xl">
          A new key.<br />
          <span className="font-black">The same protection.</span>
        </h1>
      </AuthHeroPanel>

      <main
        className="col-span-1 flex items-center justify-center px-6 py-12 md:px-16 lg:col-span-3"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <img
            src={E1_LOGO}
            alt="EmployeesFirst"
            className="mb-10 h-12 max-w-[260px] w-auto object-contain lg:hidden"
            width="260"
            height="32"
            data-testid="reset-password-mobile-logo"
          />
          <Link
            to="/login"
            className="mb-10 inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: "var(--ink)" }}
            data-testid="reset-password-back-link"
          >
            <ArrowLeft size={16} /> Back To Sign In
          </Link>

          <div className="eyebrow mb-3">Choose a new password</div>
          <h2
            className="font-heading text-4xl font-black leading-none md:text-5xl"
            style={{ color: "var(--ink)" }}
            data-testid="reset-password-heading"
          >
            Secure your account.
          </h2>

          {complete ? (
            <div
              className="mt-8 border p-6"
              style={{ borderColor: "var(--border-strong)" }}
            >
              <CheckCircle2 size={24} style={{ color: "var(--accent)" }} />
              <p
                className="mt-4 text-base font-semibold"
                style={{ color: "var(--ink)" }}
                data-testid="reset-password-confirmation"
              >
                Your password has been updated.
              </p>
              <p
                className="mt-2 text-sm leading-relaxed"
                style={{ color: "var(--muted)" }}
              >
                Sign in with the new password, then complete the usual email verification step.
              </p>
              <Link
                to="/login"
                className="btn-primary mt-6 inline-flex items-center gap-2"
                data-testid="reset-password-sign-in-link"
              >
                Continue To Sign In <KeyRound size={16} />
              </Link>
            </div>
          ) : (
            <>
              <p className="mb-10 mt-3 text-sm" style={{ color: "var(--muted)" }}>
                Choose a new password of at least 12 characters. This recovery link works once.
              </p>

              {(error || linkMissing) && (
                <div
                  className="pill-danger mb-6 flex items-start gap-2 p-3 text-sm"
                  data-testid="reset-password-error"
                >
                  <ShieldAlert size={16} className="mt-0.5" />
                  <span>
                    {linkMissing ? "This recovery link is incomplete. Request a new one." : error}
                  </span>
                </div>
              )}

              <form
                onSubmit={submitReset}
                className="space-y-8"
                data-testid="reset-password-form"
              >
                <div>
                  <label className="field-label" htmlFor="new-password">
                    New password
                  </label>
                  <PasswordField
                    id="new-password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    autoComplete="new-password"
                    minLength={12}
                    disabled={linkMissing}
                    required
                    testId="reset-password-new-input"
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="confirm-password">
                    Confirm new password
                  </label>
                  <PasswordField
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    autoComplete="new-password"
                    minLength={12}
                    disabled={linkMissing}
                    required
                    testId="reset-password-confirm-input"
                  />
                  <PasswordChecklist
                    password={newPassword}
                    confirm={confirmPassword}
                    testId="reset-password-checklist"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || linkMissing || !isPasswordValid(newPassword, { confirm: confirmPassword })}
                  className="btn-primary flex w-full items-center justify-center gap-2"
                  data-testid="reset-password-submit-button"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <>
                      Update Password <KeyRound size={16} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}